/**
 * Share Image Generator for TasteInsights
 *
 * Generates shareable badge images from TasteInsights data.
 * Uses html-to-image for DOM-to-image conversion.
 */

import { toPng, toJpeg, toBlob } from 'html-to-image'
import type { TasteInsights, TasteInsightsFriend } from '@/types/tasteInsights'
import { getRarityColors, getRarityTier, getRarityDisplayName, getRarityEmoji } from './rarityCalculator'
import { FavoriteCuisine, getCuisineColorInfo } from './cuisineColorMapping'
import logoImage from '@/assets/images/logo.png'

// Cache for logo base64
let logoBase64Cache: string | null = null

/**
 * Convert the logo image to base64 for embedding in the share image
 */
async function getLogoBase64(): Promise<string> {
  if (logoBase64Cache) {
    return logoBase64Cache
  }

  try {
    const response = await fetch(logoImage)
    const blob = await response.blob()
    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.onloadend = () => {
        logoBase64Cache = reader.result as string
        resolve(logoBase64Cache)
      }
      reader.onerror = reject
      reader.readAsDataURL(blob)
    })
  } catch (error) {
    console.error('Failed to load logo:', error)
    return ''
  }
}

export interface ShareImageOptions {
  width?: number
  height?: number
  quality?: number
  format?: 'png' | 'jpeg'
  pixelRatio?: number
}

const DEFAULT_OPTIONS: ShareImageOptions = {
  width: 1080,
  height: 1080,
  quality: 0.95,
  format: 'png',
  pixelRatio: 2, // For retina displays
}

/**
 * Generate a shareable image blob from a DOM element
 */
export async function generateImageFromElement(
  element: HTMLElement,
  options: ShareImageOptions = {}
): Promise<Blob> {
  const opts = { ...DEFAULT_OPTIONS, ...options }

  const imageOptions = {
    width: opts.width,
    height: opts.height,
    quality: opts.quality,
    pixelRatio: opts.pixelRatio,
    cacheBust: true,
    // Preserve fonts
    fontEmbedCSS: undefined,
    // Skip external images that might fail
    skipFonts: false,
    // Background color
    backgroundColor: '#1A1A2E',
  }

  if (opts.format === 'jpeg') {
    const dataUrl = await toJpeg(element, imageOptions)
    return dataUrlToBlob(dataUrl)
  }

  const blob = await toBlob(element, imageOptions)
  if (!blob) {
    throw new Error('Failed to generate image blob')
  }
  return blob
}

/**
 * Generate a TasteInsights badge image from insights data
 * This creates a temporary DOM element, renders it, and captures it as an image
 */
export async function generateTasteInsightsBadgeImage(
  tasteInsights: TasteInsights | TasteInsightsFriend,
  userName: string,
  favoriteCuisines: FavoriteCuisine[] = [],
  options: ShareImageOptions = {}
): Promise<Blob> {
  // Create a temporary container
  const container = document.createElement('div')
  container.style.position = 'fixed'
  container.style.left = '-9999px'
  container.style.top = '-9999px'
  document.body.appendChild(container)

  try {
    // Generate the badge HTML
    const badgeHtml = await createBadgeHtml(tasteInsights, userName, favoriteCuisines)
    container.innerHTML = badgeHtml

    // Wait for fonts and styles to load
    await new Promise((resolve) => setTimeout(resolve, 100))

    // Get the badge element
    const badgeElement = container.firstElementChild as HTMLElement
    if (!badgeElement) {
      throw new Error('Failed to create badge element')
    }

    // Generate the image
    const blob = await generateImageFromElement(badgeElement, options)
    return blob
  } finally {
    // Clean up
    document.body.removeChild(container)
  }
}

/**
 * Generate a TasteAura gradient from favorite cuisines
 */
function generateTasteAuraGradientForImage(cuisines: FavoriteCuisine[]): string {
  if (!cuisines || cuisines.length === 0) {
    // Default Paire gradient
    return 'linear-gradient(135deg, #1A1A2E 0%, #16213E 50%, #0F3460 100%)'
  }

  // Get colors from top cuisines (max 3 for gradient)
  const topCuisines = cuisines.slice(0, 3)
  const colors = topCuisines.map((c) => getCuisineColorInfo(c.name))

  // Darken colors for better text readability (mix with dark base)
  const darken = (hex: string, amount: number = 0.6): string => {
    const r = parseInt(hex.slice(1, 3), 16)
    const g = parseInt(hex.slice(3, 5), 16)
    const b = parseInt(hex.slice(5, 7), 16)
    const darkR = Math.round(r * amount)
    const darkG = Math.round(g * amount)
    const darkB = Math.round(b * amount)
    return `rgb(${darkR}, ${darkG}, ${darkB})`
  }

  if (colors.length === 1) {
    const c = colors[0]
    return `linear-gradient(135deg, ${darken(c.primaryColor, 0.4)} 0%, ${darken(c.secondaryColor, 0.35)} 50%, ${darken(c.accentColor, 0.3)} 100%)`
  }

  if (colors.length === 2) {
    return `linear-gradient(135deg, ${darken(colors[0].primaryColor, 0.4)} 0%, ${darken(colors[0].secondaryColor, 0.35)} 33%, ${darken(colors[1].primaryColor, 0.35)} 66%, ${darken(colors[1].secondaryColor, 0.3)} 100%)`
  }

  // Three cuisines
  return `linear-gradient(135deg, ${darken(colors[0].primaryColor, 0.4)} 0%, ${darken(colors[1].primaryColor, 0.35)} 50%, ${darken(colors[2].primaryColor, 0.3)} 100%)`
}

/**
 * Create the HTML for the shareable badge
 */
async function createBadgeHtml(
  tasteInsights: TasteInsights | TasteInsightsFriend,
  userName: string,
  favoriteCuisines: FavoriteCuisine[] = []
): Promise<string> {
  const logoBase64 = await getLogoBase64()
  const rarityTier = getRarityTier(tasteInsights)
  const rarityColors = getRarityColors(rarityTier)

  const cuisines = tasteInsights.insights?.globalWheelhouse?.slice(0, 3) || []
  const diningStyles = tasteInsights.insights?.diningStyles?.slice(0, 2) || []

  // Generate dynamic background based on favorite cuisines
  const backgroundGradient = generateTasteAuraGradientForImage(favoriteCuisines)

  return `
    <div style="
      width: 1080px;
      height: 1080px;
      background: ${backgroundGradient};
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      padding: 60px;
      box-sizing: border-box;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      color: white;
      position: relative;
      overflow: hidden;
    ">
      <!-- Background decoration -->
      <div style="
        position: absolute;
        top: -100px;
        right: -100px;
        width: 400px;
        height: 400px;
        background: radial-gradient(circle, rgba(124, 58, 237, 0.3) 0%, transparent 70%);
        border-radius: 50%;
      "></div>
      <div style="
        position: absolute;
        bottom: -150px;
        left: -150px;
        width: 500px;
        height: 500px;
        background: radial-gradient(circle, rgba(255, 107, 53, 0.2) 0%, transparent 70%);
        border-radius: 50%;
      "></div>

      <!-- Paire logo -->
      <div style="
        position: absolute;
        top: 40px;
        left: 50%;
        transform: translateX(-50%);
      ">
        <img src="${logoBase64}" alt="Paire" style="height: 75px; width: auto;" />
      </div>

      <!-- Rarity badge -->
      <div style="
        position: absolute;
        top: 40px;
        right: 40px;
        padding: 14px 28px;
        background: ${rarityColors.bg};
        border-radius: 30px;
        font-size: 18px;
        font-weight: 700;
        color: ${rarityColors.textColor};
        text-transform: uppercase;
        letter-spacing: 2px;
        box-shadow: ${rarityColors.glow};
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 8px;
        line-height: 1;
      "><span style="font-size: 20px;">${getRarityEmoji(rarityTier)}</span><span>${getRarityDisplayName(rarityTier)}</span></div>

      <!-- Main content -->
      <div style="
        text-align: center;
        z-index: 1;
        max-width: 900px;
      ">
        <!-- User name - prominent -->
        <div style="
          font-size: 48px;
          font-weight: 800;
          margin-bottom: 16px;
          color: white;
          line-height: 1.2;
        ">${escapeHtml(userName)}</div>

        <!-- TasteTribe name -->
        <div style="
          font-size: 64px;
          font-weight: 800;
          margin-bottom: 20px;
          background: linear-gradient(135deg, #7C3AED 0%, #FF6B35 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
          line-height: 1.2;
        ">${escapeHtml(tasteInsights.tasteTribe || 'TasteInsights')}</div>

        <!-- TasteTribe emojis -->
        <div style="
          font-size: 80px;
          margin-bottom: 30px;
          line-height: 1;
        ">${tasteInsights.tasteTribeEmojis || '🍽️'}</div>

        <!-- Insight snippet with quote style -->
        <div style="
          position: relative;
          background: rgba(255, 255, 255, 0.08);
          border-radius: 12px;
          padding: 24px 32px;
          margin-bottom: 50px;
          max-width: 800px;
          border-left: 4px solid rgba(124, 58, 237, 0.6);
        ">
          <!-- Decorative quote icon -->
          <div style="
            position: absolute;
            top: 8px;
            right: 16px;
            font-size: 80px;
            color: rgba(255, 255, 255, 0.12);
            line-height: 1;
            font-family: Georgia, serif;
          ">"</div>
          <div style="
            font-size: 28px;
            color: rgba(255, 255, 255, 0.9);
            font-style: italic;
            line-height: 1.5;
            position: relative;
            z-index: 1;
          ">${escapeHtml(tasteInsights.insightSnippet || '')}</div>
        </div>

        <!-- Tags row -->
        <div style="
          display: flex;
          flex-wrap: wrap;
          justify-content: center;
          gap: 16px;
          margin-bottom: 40px;
        ">
          ${cuisines
            .map(
              (cuisine) => `
            <div style="
              padding: 12px 24px;
              background: rgba(124, 58, 237, 0.3);
              border: 1px solid rgba(124, 58, 237, 0.5);
              border-radius: 30px;
              font-size: 20px;
              color: white;
            ">${escapeHtml(cuisine)}</div>
          `
            )
            .join('')}
        </div>

        <!-- Dining styles -->
        ${
          diningStyles.length > 0
            ? `
        <div style="
          display: flex;
          flex-wrap: wrap;
          justify-content: center;
          gap: 12px;
        ">
          ${diningStyles
            .map(
              (style) => `
            <div style="
              padding: 10px 20px;
              background: rgba(255, 107, 53, 0.2);
              border: 1px solid rgba(255, 107, 53, 0.4);
              border-radius: 20px;
              font-size: 18px;
              color: rgba(255, 255, 255, 0.9);
            ">${escapeHtml(style)}</div>
          `
            )
            .join('')}
        </div>
        `
            : ''
        }
      </div>

      <!-- Footer branding -->
      <div style="
        position: absolute;
        bottom: 50px;
        left: 50%;
        transform: translateX(-50%);
        display: flex;
        align-items: baseline;
        gap: 8px;
      ">
        <div style="
          font-size: 22px;
          color: rgba(255, 255, 255, 0.5);
          font-weight: 500;
        ">Discover yours at</div>
        <div style="
          font-size: 24px;
          color: rgba(255, 255, 255, 0.8);
          font-weight: 700;
        ">app.paire.io</div>
      </div>
    </div>
  `
}

/**
 * Escape HTML special characters
 */
function escapeHtml(text: string): string {
  const div = document.createElement('div')
  div.textContent = text
  return div.innerHTML
}

/**
 * Convert a data URL to a Blob
 */
function dataUrlToBlob(dataUrl: string): Blob {
  const arr = dataUrl.split(',')
  const mime = arr[0].match(/:(.*?);/)?.[1] || 'image/png'
  const bstr = atob(arr[1])
  let n = bstr.length
  const u8arr = new Uint8Array(n)
  while (n--) {
    u8arr[n] = bstr.charCodeAt(n)
  }
  return new Blob([u8arr], { type: mime })
}

/**
 * Download an image blob as a file
 */
export async function downloadShareImage(blob: Blob, filename: string): Promise<void> {
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(url)
}

/**
 * Copy an image blob to the clipboard (if supported)
 */
export async function copyImageToClipboard(blob: Blob): Promise<boolean> {
  try {
    if (!navigator.clipboard?.write) {
      console.warn('Clipboard API not supported')
      return false
    }

    await navigator.clipboard.write([
      new ClipboardItem({
        [blob.type]: blob,
      }),
    ])
    return true
  } catch (error) {
    console.error('Failed to copy image to clipboard:', error)
    return false
  }
}

/**
 * Share image using native share API
 */
export async function shareImageNatively(
  blob: Blob,
  title: string,
  text: string
): Promise<boolean> {
  try {
    if (!navigator.share || !navigator.canShare) {
      return false
    }

    const file = new File([blob], 'taste-profile.png', { type: blob.type })

    const shareData = {
      title,
      text,
      files: [file],
    }

    if (!navigator.canShare(shareData)) {
      return false
    }

    await navigator.share(shareData)
    return true
  } catch (error) {
    // User cancelled or error occurred
    if ((error as Error).name !== 'AbortError') {
      console.error('Failed to share:', error)
    }
    return false
  }
}

/**
 * Check if native sharing with files is supported
 */
export function isNativeShareSupported(): boolean {
  if (!navigator.share || !navigator.canShare) {
    return false
  }

  // Test with a dummy file
  try {
    const testFile = new File([''], 'test.png', { type: 'image/png' })
    return navigator.canShare({ files: [testFile] })
  } catch {
    return false
  }
}

/**
 * Copy a link to clipboard
 */
export async function copyLinkToClipboard(url: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(url)
    return true
  } catch (error) {
    console.error('Failed to copy link:', error)
    return false
  }
}
